import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { Collapse } from "@/components/design/Collapse";
import { Disclosure } from "@/components/design/Disclosure";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { AgentMarkdown } from "@/components/chat/AgentMarkdown";
import { downloadBlob } from "@/lib/file-download";
import { MAX_YAML_IMPORT_BYTES, formatMegabytes } from "@/lib/upload-limits";
import {
  additionalExamplesForKind,
  selectExamplesForKind,
  splitImportGuide,
  type ImportGuideSection,
  type ImportSpecExample,
  type ImportSpecKind,
} from "./import-specification";
import { useImportSpecification } from "./use-import-specification";

/**
 * The collapsible "what does the file have to look like" panel.
 *
 * Every byte shown or downloaded here comes from `Query.importSpecification`,
 * which genesis generates from the importer that will actually run the file.
 * Spark keeps no field list, no example and no schema of its own, so the panel
 * cannot document a different build than the one that is deployed. The single
 * exception is the upload limit: that ceiling is Spark's transport, not the
 * importer's format, and genesis cannot know it.
 */

const KINDS: ImportSpecKind[] = ["Person", "SharedItem"];

/** An anchor jump requested by the page; the nonce distinguishes repeated clicks. */
export interface RequestedAnchor {
  anchor: string;
  nonce: number;
}

function GuideSection({
  section,
  requestedAnchor,
}: {
  section: ImportGuideSection;
  requestedAnchor?: RequestedAnchor | null;
}) {
  // Same card shape as ExampleRow below, so chapters and examples read as one
  // list. Collapsed by default: the closed titles are the guide's table of
  // contents, and the page owns all scrolling once a chapter is open.
  const [open, setOpen] = useState(false);
  // Error guidance links jump here by anchor; the chapter must open itself,
  // because a closed Collapse leaves the scroll target invisible. Sub-chapter
  // anchors (per document kind) open their owning chapter. The nonce re-opens
  // it when the same link is clicked again after a manual close, and the
  // chapter scrolls itself into view because the rendered markdown carries no
  // ids the page could target.
  useEffect(() => {
    if (!requestedAnchor) return;
    if (requestedAnchor.anchor !== section.anchor && !section.subAnchors.includes(requestedAnchor.anchor)) return;
    setOpen(true);
    window.requestAnimationFrame(() => {
      document.getElementById(section.anchor)?.scrollIntoView?.({ behavior: "smooth" });
    });
  }, [requestedAnchor, section.anchor, section.subAnchors]);
  return (
    <div id={section.anchor} className="mt-2 rounded-md bg-ink/[0.02] ring-1 ring-ink/10">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="hover:bg-ink/[0.04] flex w-full items-center gap-2 rounded-md p-3 text-left transition"
      >
        <span
          className={`shrink-0 text-[10px] transition-transform duration-[var(--duration-base)] ${open ? "rotate-90" : ""}`}
        >
          ›
        </span>
        <span className="min-w-0 text-[12px] font-medium text-ink/80">{section.title}</span>
      </button>
      <Collapse open={open}>
        <div className="px-3 pb-3 text-[12px] text-ink/75">
          <AgentMarkdown content={section.body} />
        </div>
      </Collapse>
    </div>
  );
}

function ExampleRow({
  example,
  onDownload,
}: {
  example: ImportSpecExample;
  onDownload: (example: ImportSpecExample) => void;
  }) {
  // Collapsed by default: three example payloads at once are a wall of YAML,
  // and the description line already says what each one shows. The bytes are
  // one click away, for reading here or for downloading.
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2 rounded-md bg-ink/[0.02] p-3 ring-1 ring-ink/10">
      {/* No flex-wrap: the buttons keep their place on the right no matter how
          long a description runs, so three cards in a row read as one shape. */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[12px] font-medium text-ink/80">{example.description}</div>
          <div className="font-mono text-[10px] text-ink/40">{example.fileName}</div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {open ? (
              <FormattedMessage id="service.page.bulkYaml.hideYaml" defaultMessage="Hide YAML" />
            ) : (
              <FormattedMessage id="service.page.bulkYaml.showYaml" defaultMessage="Show YAML" />
            )}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => onDownload(example)}>
            <FormattedMessage
              id="service.page.bulkYaml.downloadYaml"
              defaultMessage="Download YAML example"
            />
          </Button>
        </div>
      </div>
      <Collapse open={open}>
        <pre className="mt-2 max-h-48 overflow-auto rounded bg-ink/[0.04] p-2 font-mono text-[10px] leading-relaxed text-ink/70">
          {example.yaml}
        </pre>
      </Collapse>
    </div>
  );
}

export function ImportSpecificationPanel({
  open,
  onToggle,
  requestedAnchor,
}: {
  open?: boolean;
  onToggle?: () => void;
  requestedAnchor?: RequestedAnchor | null;
}) {
  const intl = useIntl();
  const [kind, setKind] = useState<ImportSpecKind>("Person");
  const { spec, limits, error } = useImportSpecification();

  const examples = useMemo(
    () => (spec ? selectExamplesForKind(spec.examples, kind) : { minimal: null, full: null }),
    [spec, kind],
  );
  const extras = useMemo(
    () => (spec ? additionalExamplesForKind(spec.examples, kind) : []),
    [spec, kind],
  );
  const guide = useMemo(() => (spec?.markdown ? splitImportGuide(spec.markdown) : null), [spec]);

  const downloadExample = (example: ImportSpecExample) => {
    // Exactly the delivered bytes, no re-serialization.
    downloadBlob(new Blob([example.yaml], { type: "application/yaml" }), example.fileName);
  };

  const downloadSchema = () => {
    if (!spec) return;
    downloadBlob(
      new Blob([spec.jsonSchema], { type: "application/schema+json" }),
      `import-${spec.formatVersion}.schema.json`,
    );
  };

  return (
    <Disclosure
      className="mt-6 @lg:max-w-5xl"
      open={open}
      onToggle={onToggle}
      title={
        <FormattedMessage id="service.page.bulkYaml.formatTitle" defaultMessage="Format and examples" />
      }
    >
      {/* This panel is reference material, not the import itself. A failure here
          gets its own specific copy rather than the generic serverError string, so
          it can never be mistaken for the import having failed. No server text is
          rendered either way (@adr SPARK-0010-no-raw-server-errors-in-ui@1). */}
      {error ? (
        <div className="text-[12px] text-ink/55">
          <FormattedMessage
            id="service.page.bulkYaml.formatLoadFailed"
            defaultMessage="The format description could not be loaded."
          />
        </div>
      ) : !spec ? (
        <div className="text-[12px] text-ink/45">
          <FormattedMessage id="service.page.bulkYaml.formatLoading" defaultMessage="Loading format …" />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <SegmentedControl
              aria-label={intl.formatMessage({
                id: "service.page.bulkYaml.formatKindLabel",
                defaultMessage: "Document kind",
              })}
              size="kpi"
              value={kind}
              onChange={setKind}
              options={KINDS.map((value) => ({ value, label: value }))}
            />
            <Button variant="secondary" size="sm" onClick={downloadSchema}>
              <FormattedMessage
                id="service.page.bulkYaml.downloadSchema"
                defaultMessage="Download JSON schema"
              />
            </Button>
          </div>

          {/* Two ceilings, and they are not the same number: genesis admits what
              its format allows, Spark can only hand over what the GraphQL proxy
              carries (MAX_YAML_IMPORT_BYTES, see lib/upload-limits.ts). Naming
              only the format limit would promise a file size no upload here can
              reach, so the effective one is always stated - and it stands even
              when the generated spec carries no limits block at all. */}
          {limits ? (
            <p className="mt-2 text-[11px] text-ink/45">
              <FormattedMessage
                id="service.page.bulkYaml.formatLimits"
                defaultMessage="Format {version} · at most {docs, number} documents per file · genesis format limit {size}."
                values={{
                  version: spec.formatVersion,
                  docs: limits.maxTopLevelDocuments,
                  size: limits.maxPayloadSize,
                }}
              />
            </p>
          ) : null}
          <p className="mt-1 text-[11px] text-ink/70">
            <FormattedMessage
              id="service.page.bulkYaml.formatUploadLimit"
              defaultMessage="Spark upload limit: {size} per file. Larger files are rejected before the import starts."
              values={{ size: formatMegabytes(MAX_YAML_IMPORT_BYTES) }}
            />
          </p>

          {/* The prose genesis generates alongside the schema - the only
              documentation this panel shows. Spark used to parse a field list
              out of the JSON schema next to it, which was the same information
              twice and the weaker of the two: two kinds instead of all of them,
              no types, and only the top level, so `content.name.de` never
              appeared. The guide carries the field tables plus what no field
              list can say (when to merge rather than insert, how a person is
              identified, what a validating run does).

              Rendered as one collapsible card per `##` chapter rather than one
              nested scrollbox: the closed titles give the overview, an open
              chapter takes the room it needs and the page scrolls. When the
              generator stops emitting chapters, the text renders whole - split
              gracefully degrades, it never drops prose.

              The kind switch above does not filter this text; it only selects
              the examples, which are per kind. */}
          {guide ? (
            guide.sections.length > 0 ? (
              <div className="mt-3">
                {guide.intro ? (
                  <div className="mb-2 text-[12px] text-ink/75">
                    <AgentMarkdown content={guide.intro} />
                  </div>
                ) : null}
                {guide.sections.map((section, index) => (
                  <GuideSection key={`${index}-${section.title}`} section={section} requestedAnchor={requestedAnchor} />
                ))}
              </div>
            ) : (
              <div className="mt-3 text-[12px] text-ink/75">
                <AgentMarkdown content={spec.markdown} />
              </div>
            )
          ) : (
            <p className="mt-2 text-[12px] text-ink/45">
              <FormattedMessage
                id="service.page.bulkYaml.formatUnavailable"
                defaultMessage="The deployed importer did not describe this kind."
              />
            </p>
          )}

          {examples.minimal ? (
            <ExampleRow example={examples.minimal} onDownload={downloadExample} />
          ) : null}
          {examples.full ? <ExampleRow example={examples.full} onDownload={downloadExample} /> : null}
          {extras.map((example) => (
            <ExampleRow key={example.fileName} example={example} onDownload={downloadExample} />
          ))}
        </>
      )}
    </Disclosure>
  );
}
